import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from 'react-router-dom';

// ** MUI Components
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import MuiCard from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import InputAdornment from '@mui/material/InputAdornment'
import MuiFormControlLabel from '@mui/material/FormControlLabel'
import { styled, useTheme } from '@mui/material/styles'
import CircularProgress from '@mui/material/CircularProgress';
import Backdrop from '@mui/material/Backdrop';

// ** Icon Imports
import AppleIcon from '@mui/icons-material/Apple';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import LinearProgress from '@mui/material/LinearProgress';
import AuthIllustrationV1Wrapper from '../login/AuthIllustrationV1Wrapper.js'
import BlankLayout from '../../Layout/blankLayout.js'
import { dev_baseUrl } from "../../../utils/var.js";
import { GoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import useAuth from "../../../hooks/useAuth.js";
import CustomTextField from '../../../components/mui/text-field'
import { useGoogleLogin } from '@react-oauth/google';
import googleicon from 'images/googleicon.png';

// ** Styled Components
const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: { width: '25rem' }
}))

const LinkStyled = styled(Link)(({ theme }) => ({
  textDecoration: 'underlined',
  color: `${theme.palette.primary.main} !important`
}))

const ExternalLinkStyled = styled('a')(({ theme }) => ({
  textDecoration: 'underline',
  color: `${theme.palette.primary.main} !important`,
  fontSize: 'inherit'
}))

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(1.75),
  '& .MuiFormControlLabel-label': {
    color: theme.palette.text.secondary
  }
}))

const Register = () => {

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const [message, setMessage] = useState(location.state?.message || '');

  const { login, logout } = useAuth();
  const { instance } = useMsal();

  const navigate = useNavigate();

  const theme = useTheme();
  const [values, setValues] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    showConfirmPassword: false,
    showPassword: false,
    errorPassword: null,
    errorConfirmPassword: null,
    errorEmail: null,
    termsAccepted: false
  });

  const user = {fullName:'', userName:'', teamName:'', roleName:'', jobTitle:'', crewLeader:''}; 

  const handleChange = prop => event => {
    if (prop === 'termsAccepted') {
      setValues({ ...values, [prop]: event.target.checked });
    } else {
      setValues({ ...values, [prop]: event.target.value });
      setErrors({ ...errors, [prop]: '' });
    }
  };

  useEffect(() => {
    logout();
  }, []);

  useEffect(() => {
    if (message || Object.keys(errors).length > 0) {
      const timer = setTimeout(() => {
        setMessage('');
        setErrors({});
        setValues(prevValues => ({
          ...prevValues,
          errorEmail: null,
          errorPassword: null,
          errorConfirmPassword: null
        }));
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [message, errors]);

  useEffect(() => {

    setValues({
      email: '', 
      password: '', 
      confirmPassword: ''
    })
    
  },[])

  const handleClickShowPassword = () => {
    setValues({ ...values, showPassword: !values.showPassword });
  };

  const handleConfirmPasswordChange = prop => event => {
    setValues({ ...values, [prop]: event.target.value })
  }

  const handleClickShowConfirmPassword = () => {
    setValues({ ...values, showConfirmPassword: !values.showConfirmPassword })
  }
  

  const validatePassword = password => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#^("'/:;\\\[\]`{|}~)_\-+=<>.,])[A-Za-z\d@$!%*?&#^("'/:;\\\[\]`{|}~)_\-+=<>.,]{8,}$/;
    return regex.test(password);
  };

 

  const checkPasswordGuidelines = (password) => {
    return {
      length: password.length >= 8,
      upperCase: /[A-Z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[@$!%*?&#^("'/:;\\\[\]`{|}~)_\-+=<>.,]/.test(password)
    }
  }

  const calculatePasswordStrength = (guidelines) => {
    const criteria = Object.values(guidelines).filter(value => value).length;
    const processedCriteria = (criteria / 4) * 100;
    return processedCriteria;
  };

  const passwordGuidelines = checkPasswordGuidelines(values.password);
  const passwordStrength = calculatePasswordStrength(passwordGuidelines);

  const getColor = (passwordStrength) => {
    if (passwordStrength === 100) {
      return 'green';
    } else if (passwordStrength >= 75) {
      return 'orange';
    } else if (passwordStrength >= 50) {
      return 'orange';
    } else {
      return 'red';
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    const { email, password, confirmPassword, termsAccepted } = values;
    const newErrors = {};

    if (!email) {
      newErrors.email = 'Please enter a valid email address.';
    }

    if (!password) {
      newErrors.password = 'Please enter a valid password.';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password.';
    }

    if (confirmPassword != password) {
      newErrors.confirmPassword = 'Passwords do not match.';
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (email && !emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email format.';       
    } 

    if (password && !validatePassword(password)) {
      // setValues({ ...values, errorPassword: "Password does not meet criteria" });
      // return;
      newErrors.password = "Password does not meet criteria";
    }

    // const value = termsAccepted ? e.target.checked : e.target.value;
    // newErrors.form = "Please accept terms and conditions before proceeding.";
    if (!termsAccepted) {
      newErrors.form = "Please accept the terms and conditions before proceeding.";
    }

    // setValues({ ...values, ['termsAccepted']: value });

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      return;
    }
    
    setLoading(true);
  
    try {
      const response = await fetch(`${dev_baseUrl}/account/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          firstName: "",
          lastName: "",
          email: email,
          password: password,
          confirmPassword: confirmPassword
        })
      });

      const data = await response.json();

      if (response.ok && !data.isError) {
        navigate('/verify-email', { state: { email: email } });
      } else {
        setValues({ ...values, errorEmail: data.message });
      }
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    
    if (credentialResponse.credential) {
      
      const token = credentialResponse.credential;
      
      setLoading(true);
      
      try {
        const response = await fetch(`${dev_baseUrl}/Account/authenticate-sso`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ provider: 'Google', token }),
        });

        const data = await response.json();
        console.log('Google SSO response:', data);

        if (response.ok && !data.isError) {

          login(data.result);
          navigate('/dashboard');
        } else {
          setErrors({ form: data.message || 'Google SSO login failed' });
          console.log('Google SSO error:', data.message || 'Google SSO login failed');
        }
      } catch (error) {
        console.error('Error:', error);
        setErrors({ form: 'An error occurred with Google SSO. Please try again later.' });
      } finally {
        setLoading(false);
      }
    }
  };

 const loginWithGoogle = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: () => setErrors({ form: 'Google login failed' }),
    });
    
  const handleHotmailLogin = async () => {
    const loginRequest = {
      scopes: ["openid", "profile", "User.Read"],
    };
    
    setLoading(true);
    
    try {
      const loginResponse = await instance.loginPopup(loginRequest);

      console.log("Login Response:", loginResponse);

      const token = loginResponse.idToken;
      console.log("Token:", token);

      const response = await fetch(`${dev_baseUrl}/Account/authenticate-sso`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ provider: 'Microsoft', token }),
      });

      const data = await response.json();
      console.log('Hotmail SSO response:', data);

      if (response.ok && !data.isError) {        
        login(data.result);
        navigate('/dashboard');
      } else {
        setErrors({ form: data.message || 'Hotmail SSO login failed' });
        console.log('Hotmail SSO error:', data.message || 'Hotmail SSO login failed');
      }
    } catch (error) {
      console.error('Error:', error);
      setErrors({ form: 'An error occurred with Hotmail SSO. Please try again later.' });
    } finally {
      setLoading(false);
    }
  };

  const divider = (              
    <Divider
      sx={{
        color: 'text.disabled',
        '& .MuiDivider-wrapper': { px: 6 },
        fontSize: theme.typography.body2.fontSize,
        my: theme => `${theme.spacing(6)} !important`,
      }}
    >
      or
    </Divider>
    );

  return (
    <BlankLayout>
      <Box className='content-center'>
        <AuthIllustrationV1Wrapper>
          <Card variant='outlined'>
          <CardContent sx={{ p: theme => `${theme.spacing(10.5, 8, 8)} !important` }}>
              <Box sx={{ mb: 8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img height='45' alt='error-illustration' src='/images/Logo2.svg' />
              </Box>
              <Box sx={{ mb: 6, textAlign: 'center' }}>
                <Typography variant='h4' sx={{ mb: 1.5 }}>
                  Create an account
                </Typography>
              </Box>

              {message && (
                <Box mb={2} textAlign="center">
                  <Typography variant="h6" color="green">{message}</Typography>
                </Box>
              )}
              {errors.form && (
                <Box mb={2} textAlign="center">
                  <Typography variant="body1" color="error">{errors.form}</Typography>
                </Box>
              )}
                
              <form noValidate autoComplete='off' onSubmit={handleSubmit}>
                <CustomTextField
                  autoFocus
                  fullWidth
                  id='email'
                  label='Email'
                  sx={{ mb: 4 }}
                  placeholder='example@gmail.com'
                  value={values.email}
                  onChange={handleChange('email')}
                  error={!!errors.email}
                  helperText={errors.email}
                  autoComplete="new-password"
                />
                
                  {values.errorEmail && (
                    <Typography color='error'>
                      {values.errorEmail}
                    </Typography>
                  )} 

                <CustomTextField
                  fullWidth
                  sx={{ mb: 1.5 }}
                  label='Password'
                  value={values.password}
                  id='auth-login-password'
                  placeholder='············'
                  onChange={handleChange('password')}
                  error={!!errors.password}
                  helperText={errors.password}
                  type={values.showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onClick={handleClickShowPassword}
                          onMouseDown={e => e.preventDefault()}
                          aria-label='toggle password visibility'
                        >
                          {values.showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                {values.errorPassword && (
                  <Typography color='error' sx={{ mb: 2 }}>
                    {values.errorPassword}
                  </Typography>
                )}
                <CustomTextField
                  fullWidth
                  label='Confirm Password'
                  placeholder='············'
                  sx={{ display: 'flex', mb: 1.5 }}
                  value={values.confirmNewPassword}
                  id='auth-reset-password-confirm-password'
                  type={values.showConfirmPassword ? 'text' : 'password'}
                  onChange={handleConfirmPasswordChange('confirmPassword')}
                  autoComplete="new-password"
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <IconButton
                          edge='end'
                          onMouseDown={e => e.preventDefault()}
                          aria-label='toggle password visibility'
                          onClick={handleClickShowConfirmPassword}
                        >
                          {values.showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
                {values.confirmPassword && (
                  <Typography color='error' sx={{ mb: 2 }}>
                    {values.errorConfirmPassword}
                  </Typography>
                )}
                <Box sx={{ my: 2 }}>
                  <LinearProgress value={passwordStrength} variant="determinate"
                   sx={{
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: getColor(passwordStrength)
                    },
                    height: '7px'
                  }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography sx={{ fontSize: '12px' }}>Must contain at least:</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      {passwordGuidelines.upperCase ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 upper case</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {passwordGuidelines.number ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 number</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ display: 'flex', mb: 1 }}>
                      {passwordGuidelines.specialChar ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 1 special character</Typography>
                    </Box>
                    <Box sx={{ display: 'flex' }}>
                      {passwordGuidelines.length ? (
                        <CheckCircleIcon sx={{ color: '#1FC16B', height: '17px', width: '17px', mr: 1 }} />
                      ) : (
                        <CancelIcon sx={{ color: '#99A0AE', height: '17px', width: '17px', mr: 1 }} />
                      )}
                      <Typography sx={{ fontSize: '12px' }}> 8 characters</Typography>
                    </Box>
                  </Box>
                </Box>
                <Box
                  sx={{
                    mb: 1.75,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        control={<Checkbox color='success' size='small' checked={values.termsAccepted} onChange={handleChange('termsAccepted')} />}
                        label={
                         <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
  <Typography variant='body2' fontSize='12px'>
    I agree to
  </Typography>
  <Typography
    component={ExternalLinkStyled}
    href="https://cleri.ai/terms-conditions"
    target="_blank"
    rel="noopener noreferrer"
    sx={{ ml: 1, fontSize: '11px !important' }}
  >
    Terms & Conditions
  </Typography>
  <Typography variant='body2' sx={{ fontSize: '12px', ml: 1 }}>
    and
  </Typography>
  <Typography
    component={ExternalLinkStyled}
    href="https://cleri.ai/privacy-policy"
    target="_blank"
    rel="noopener noreferrer"
    sx={{ ml: 1, fontSize: '11px !important' }}
  >
    Privacy Policy.
  </Typography>
</Box>

                        }
                      />
                  </Box>                
                </Box>
                <Button
                  fullWidth
                  variant='contained'
                  sx={{ mb: 4 }}
                  color='main'
                  type='submit'
                  disabled={loading}
                >
                  Create an account
                </Button>
              </form>
              <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', mt: 2 }}>
                <Typography sx={{ color: 'text.secondary', mr: 2 }}>Already have an account?</Typography>
                <Typography
                  component={LinkStyled}
                  to='/login'
                  
                  sx={{ fontSize: theme.typography.body1.fontSize }}
                >
                  Sign in instead
                </Typography>
              </Box>
              {loading ? null : divider}
              <Box sx={{ display: loading ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {/* <Box sx={{mx: 2}}>
                    <GoogleLogin
                      onSuccess={handleGoogleLoginSuccess}
                      onError={() => {
                        console.log('Login Failed');
                      }}
                      type='icon'
                      shape='circle'
                      disabled={loading}
                    />
                  </Box> */}
                  <Box
                  sx={{
                    border: "1px solid #dadce0",
                    borderRadius: "20px",
                    mx: 2,
                  }}
                >
                  <IconButton
                    onClick={() => loginWithGoogle()}
                    sx={{
                      backgroundColor: "transparent",
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                    disabled={loading}
                  >
                    <img
                      src={googleicon}
                      alt="google-logo"
                      style={{ width: 20, height: 20 }}
                    />
                  </IconButton>
                </Box>
                  {/* <Box sx={{border: '1px solid #dadce0', borderRadius: '20px', mx: 2}}>
                    <IconButton
                      href='/'
                      component={'a'}
                      sx={{ color: '#272727' }}
                      onClick={e => e.preventDefault()}
                      disabled={loading}
                    >
                      <AppleIcon />
                    </IconButton>
                  </Box> */}
                  {/* <Box sx={{border: '1px solid #dadce0', borderRadius: '20px', mx: 2}}>
                    <IconButton
                      onClick={handleHotmailLogin}
                      sx={{ color: theme => (theme.palette.mode === 'light' ? '#272727' : 'grey.300') }}
                      disabled={loading}
                    >
                      <img src='/images/icon-outlook.png' alt='outlook-logo' />
                    </IconButton>
                  </Box> */}
                </Box>
            </CardContent>
          </Card>
        </AuthIllustrationV1Wrapper>
      </Box>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </BlankLayout>
  );
};

export default Register;
